import { useQuery } from '@tanstack/react-query';
import { Users, DollarSign, TrendingUp, CreditCard, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import adminClient from '@/lib/api/adminClient';
import { formatCurrency } from '@/lib/utils';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const PLAN_COLORS: Record<string, string> = {
  trial: '#9ca3af',
  starter: '#3b82f6',
  pro: '#00d084',
  expert: '#f59e0b',
  unknown: '#6b7280',
};

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => adminClient.get('/admin/stats').then((r) => r.data),
  });

  const { data: revenue } = useQuery({
    queryKey: ['admin', 'revenue'],
    queryFn: () => adminClient.get('/admin/analytics/revenue').then((r) => r.data),
  });

  const { data: userGrowth } = useQuery({
    queryKey: ['admin', 'user-growth'],
    queryFn: () => adminClient.get('/admin/analytics/user-growth?days=30').then((r) => r.data),
  });

  const { data: planDist } = useQuery({
    queryKey: ['admin', 'plan-distribution'],
    queryFn: () => adminClient.get('/admin/analytics/plan-distribution').then((r) => r.data),
  });

  const { data: revenueTrend } = useQuery({
    queryKey: ['admin', 'revenue-trend'],
    queryFn: () => adminClient.get('/admin/analytics/revenue-trend?period=monthly').then((r) => r.data),
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const statCards = [
    { label: '總用戶數', value: stats?.total_users ?? 0, icon: Users, color: 'text-blue-500' },
    { label: '活躍用戶', value: stats?.active_users ?? 0, icon: TrendingUp, color: 'text-green-500' },
    { label: '本月收入', value: formatCurrency(revenue?.revenue_this_month ?? 0), icon: DollarSign, color: 'text-amber-500' },
    { label: '交易筆數', value: revenue?.successful_transactions ?? 0, icon: CreditCard, color: 'text-purple-500' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">儀表板</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold mt-1">{value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">用戶成長趨勢（近 30 天）</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={userGrowth || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                <Line type="monotone" dataKey="count" stroke="#00d084" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Plan Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">方案分佈</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={planDist || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ plan, percentage }) => `${plan} ${percentage}%`}
                  outerRadius={90}
                  dataKey="count"
                  nameKey="plan"
                >
                  {(planDist || []).map((entry: any, index: number) => (
                    <Cell key={index} fill={PLAN_COLORS[entry.plan] || '#6b7280'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">收入趨勢（按月）</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueTrend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
              <Bar dataKey="amount" fill="#00d084" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
